import * as zExt from "@redotech/rpc/ext";
import { privacy } from "@redotech/schema-privacy";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

export enum IncidentSeverity {
  INFO = "info",
  WARNING = "warning",
  ERROR = "error",
}

export enum IncidentStatus {
  ACTIVE = "active",
  RESOLVED = "resolved",
}

export enum IncidentProduct {
  OMS = "oms",
  ALL = "all",
  MARKETING = "marketing",
  RETURNS = "returns",
  CONVERSION = "conversion",
  RECOMMERCE = "recommerce",
  SUPPORT = "support",
}

export const IncidentSchema = z.object({
  _id: zExt.objectId().meta({ privacy: privacy.nonPii }),
  title: z.string().meta({ privacy: privacy.free_text }),
  message: z.string().meta({ privacy: privacy.free_text }),
  severity: createEnum(IncidentSeverity, { enumName: "IncidentSeverity" }).meta(
    { privacy: privacy.nonPii },
  ),
  status: createEnum(IncidentStatus, { enumName: "IncidentStatus" }).meta({
    privacy: privacy.nonPii,
  }),
  type: createEnum(IncidentProduct, { enumName: "IncidentProduct" }).meta({
    privacy: privacy.nonPii,
  }),
  types: z
    .array(
      createEnum(IncidentProduct, { enumName: "IncidentProduct" }).meta({
        privacy: privacy.nonPii,
      }),
    )
    .optional(),
  affectedTeams: z
    .array(zExt.objectId().meta({ privacy: privacy.nonPii }))
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  isGlobal: z.boolean().meta({ privacy: privacy.nonPii }),
  isTest: z.boolean().meta({ privacy: privacy.nonPii }),
  adminOnly: z.boolean().optional().meta({ privacy: privacy.nonPii }),
  routeScoped: z.boolean().optional().meta({ privacy: privacy.nonPii }),
  createdAt: z.date().meta({ privacy: privacy.nonPii }),
  resolvedAt: z.date().nullish().meta({ privacy: privacy.nonPii }),
  updatedAt: z.date().meta({ privacy: privacy.nonPii }),
  createdBy: z.string().nullish().meta({ privacy: privacy.person_name }),
  notes: z.string().nullish().meta({ privacy: privacy.free_text }),
  publicUrl: z.string().nullish().meta({ privacy: privacy.redact }),
});
export type Incident = z.infer<typeof IncidentSchema>;
export interface IIncident extends z.ZodType<Incident> {}

const severityOrder = [
  IncidentSeverity.ERROR,
  IncidentSeverity.WARNING,
  IncidentSeverity.INFO,
];

export const incidentSort: (a: Incident, b: Incident) => number = (a, b) => {
  // severity desc, createdAt desc
  if (a.severity !== b.severity) {
    return (
      severityOrder.indexOf(a.severity) - severityOrder.indexOf(b.severity)
    );
  }
  return b.createdAt.getTime() - a.createdAt.getTime();
};
